import {
  IReportingQueryDrilldown,
  IReportTaskInfoResolved
} from '@dadohr/shared/domainTypes/reports';
import { Button, TableContainer, Typography } from '@material-ui/core';
import { compact, flatten, groupBy, mapValues, sortBy } from 'lodash';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  DataTable2Cell,
  DataTable2With2StickyColumns
} from '../../../../../components/DataTable';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { useUnsafeTranslation } from '../../../../../i18next';
import { useCurrentUser } from '../../../../../services/currentUser';
import {
  getActorSortKey,
  useExperienceTemplateById
} from '../../../../../services/experiences/template';
import { useCombineLoadingValues } from '../../../../../services/firebase';
import { toFullName } from '../../../../../services/user';
import { EMPTY_ACTOR_DESCRIPTOR } from '../../../../Experiences/components/Actors';
import {
  getRelativeAnchorDefinition,
  useAnchorFields
} from '../../../../Experiences/components/Anchors';
import { CardItemDialog } from '../../../../Experiences/components/CalendarV2/CardItemDialog';
import { getItemDisplayName } from '../../../../Experiences/helpers/adminName';
import { TimelineItemExecutionTableLazy } from '../../../../Experiences/components/CalendarV2/DialogTable';
import { ParticipantDialogLazy } from '../../../../Experiences/pages/Details/ParticipantDialog';
import { useReportsDrilldownDataByInstance } from '../../../services/data';
import { ReportDrilldownSortKeys } from '../types';
import {
  AnchorTile,
  ANCHOR_GAP_MOCK_ID_PREFIX,
  BlankTile,
  collectGapsAndSort,
  CompletionPercentageChip,
  DataTable2RowOtherData,
  gapOrAnchorOrOther,
  LEFT_COL_WIDTH,
  NameContainer,
  ReportDrilldownEmptyState,
  ReportDrilldownError,
  ReportDrilldownLoader,
  RowData,
  shortenLabel,
  shortName,
  SpacerTile,
  StatusTile,
  taskStatusToString,
  TrackingFns,
  useApplySearchAndSortRows,
  WithTooltipIfTruncated
} from './common';

export const BodyByInstance = ({
  q,
  search,
  sort,
  trackingFns
}: {
  q: IReportingQueryDrilldown;
  search: string;
  sort: ReportDrilldownSortKeys;
  trackingFns: TrackingFns;
}) => {
  const type = q.otherData?.type;
  const { org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const [vs, loading, error] = useCombineLoadingValues(
    useExperienceTemplateById(q.tplIds[0]),
    useReportsDrilldownDataByInstance(q)
  );
  const [tpl, d] = vs || [undefined, undefined];
  const [expanded, setExpanded] = useState<{ [instanceId: string]: boolean }>(
    {}
  );

  const anchorFields = useAnchorFields(org.customUserFields);
  // we just pass primitives down into the hook, to avoid
  // having a dependency on the template itself, which might change for
  // unrelated reasons
  const anchorDefinition =
    (tpl && getRelativeAnchorDefinition(tpl.data.anchor, anchorFields, t)) ||
    null;
  const anchorPosition = anchorDefinition?.position ?? null;
  const anchorName = anchorDefinition?.shortLabel ?? null;

  const [
    peopleDialogTask,
    setPeopleDialogTask
  ] = useState<null | IReportTaskInfoResolved>(null);

  // Add a lazy dialog instance somehow
  const [dialogInstance, setDialogInstance] = useState<null | {
    tplId: string;
    instanceId: string;
  }>(null);
  const allRows = useMemo<null | RowData[]>(() => {
    if (!d || d.type !== 'everyone') {
      return null;
    }
    const anchor =
      anchorPosition !== null && anchorName !== null
        ? { name: anchorName, position: anchorPosition }
        : null;
    const sortedTasks = collectGapsAndSort(
      Object.values(d.data.tasks),
      anchor,
      t
    );
    const sortedActors = sortBy(Object.values(d.data.actors), (a) =>
      getActorSortKey(a.key)
    );

    const expTitle = tpl?.data.title || '';
    const resolveToken = (s: string) =>
      s.replace(/\{\{\s*experience\.title\s*\}\}/g, expTitle);
    const resolvedTasksById: {
      [key: string]: IReportTaskInfoResolved;
    } = mapValues(d.data.tasks, (tsk) => {
      return {
        ...tsk,
        title: resolveToken(tsk.title),
        adminName: tsk.adminName != null ? resolveToken(tsk.adminName) : tsk.adminName,
        actor: d.data.actors[tsk.actor] || EMPTY_ACTOR_DESCRIPTOR(tsk.actor)
      };
    });

    const resolvedTasksByActor = groupBy(
      Object.values(resolvedTasksById),
      (tsk) => tsk.actor?.key
    );

    return flatten(
      compact(
        d.data.containers.map<RowData[] | null>((c) => {
          const user = d.data.users[c.topLevelRow.userId];
          if (!user) {
            // should never happen
            return null;
          }
          const otherData: DataTable2RowOtherData = {
            searchData: { name: toFullName(user) },
            sortData: {
              completionPercentage: c.topLevelRow.total.total
                ? c.topLevelRow.total.percentage
                : null,
              preferredName: user?.preferredName || '',
              lastName: user?.lastName || ''
            }
          };
          const exp = !!expanded[c.instanceId];
          const userShortName = shortName(user, 20);
          const userFullname = toFullName(user);
          const firstRowCells: DataTable2Cell[] = compact([
            {
              cell: (
                <NameContainer
                  spacing={1}
                  role="button"
                  onClick={() => {
                    if (!exp) {
                      trackingFns.trackRowExpanded(
                        c.templateId,
                        `${userFullname}' experience`
                      );
                    }
                    setExpanded((x) => ({ ...x, [c.instanceId]: !exp }));
                  }}
                >
                  {exp ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <div>
                    <WithTooltipIfTruncated
                      short={userShortName}
                      full={userFullname}
                    />
                    's {t('experience')}
                  </div>
                </NameContainer>
              )
            },
            {
              cell: <CompletionPercentageChip v={c.topLevelRow.total} />
            },
            ...(exp
              ? [
                  {
                    cell: (
                      <Button
                        color="primary"
                        onClick={() => {
                          trackingFns.openInstanceDialog(c.instanceId);
                          setDialogInstance({
                            tplId: c.templateId,
                            instanceId: c.instanceId
                          });
                        }}
                      >
                        {t('See details of this experience')}
                      </Button>
                    ),
                    colSpan: 7
                  },
                  ...sortedTasks.slice(7).map((tsk) => ({
                    cell: gapOrAnchorOrOther(tsk, () => <BlankTile />)
                  }))
                ]
              : sortedTasks.map((tsk) => {
                  const cell = gapOrAnchorOrOther(tsk, () => {
                    const status =
                      c.actors[tsk.actor]?.byTask[tsk.id]?.status || null;
                    // const status = c.topLevelRow.byTask[t.id]?.status || null;
                    const task = resolvedTasksById[tsk.id];

                    return (
                      <StatusTile
                        key={tsk.id}
                        status={status}
                        info={task}
                        onClick={() => {
                          trackingFns.trackBlockClick(
                            task,
                            taskStatusToString(status, t),
                            'status'
                          );
                          trackingFns.openExecutionDialog(task);
                          setPeopleDialogTask(task);
                        }}
                        onMouseEnter={() => {
                          trackingFns.trackBlockHover(
                            task,
                            taskStatusToString(status, t),
                            'status'
                          );
                        }}
                      />
                    );
                  });
                  return { cell };
                }))
          ]);

          const firstRow: RowData = {
            key: c.instanceId,
            cells: firstRowCells,
            otherData
          };

          return [
            firstRow,
            ...(exp
              ? compact([
                  ...sortedActors.map<RowData | null>((a) => {
                    const actorData = c.actors[a.key];
                    if (!actorData) {
                      return null;
                    }
                    const hasActorTasks = resolvedTasksByActor[a.key]?.length;
                    if (!hasActorTasks) {
                      return null;
                    }
                    // The default should never be needed because the backend contains
                    // fallbacks already - but the demo content seems to have some holes.
                    const actorUser = d.data.users[actorData.userId] || {
                      id: actorData.userId,
                      preferredName: 'Unknown',
                      lastName: 'User'
                    };
                    const actorNameShort = shortenLabel(a.label, 14);
                    const actorNameFull = a.label;
                    const actorUserNameShort = shortName(actorUser, 25);
                    const actorUserNameFull = toFullName(actorUser);
                    const cells: DataTable2Cell[] = [
                      {
                        cell: (
                          <NameContainer spacing={1} role="button">
                            <ChevronRight size={16} color="white" />
                            <div>
                              <strong>
                                <WithTooltipIfTruncated
                                  short={actorNameShort}
                                  full={actorNameFull}
                                />
                              </strong>{' '}
                              <WithTooltipIfTruncated
                                short={actorUserNameShort}
                                full={actorUserNameFull}
                              />
                            </div>
                          </NameContainer>
                        )
                      },
                      {
                        cell: (
                          <CompletionPercentageChip
                            v={actorData.total}
                            invertColors
                          />
                        )
                      },
                      ...sortedTasks.map((tsk) => {
                        const cell = gapOrAnchorOrOther(tsk, () => {
                          const task = resolvedTasksById[tsk.id];
                          const isSameActor =
                            actorData?.actorKey === task?.actor.key;
                          const status =
                            actorData.byTask[tsk.id]?.status || null;
                          return isSameActor ? (
                            <StatusTile
                              key={tsk.id}
                              status={status}
                              info={task}
                              onClick={() => {
                                trackingFns.trackBlockClick(
                                  task,
                                  taskStatusToString(status, t),
                                  'status'
                                );
                                trackingFns.openExecutionDialog(task);
                                setPeopleDialogTask(task);
                              }}
                              onMouseEnter={() => {
                                trackingFns.trackBlockHover(
                                  task,
                                  taskStatusToString(status, t),
                                  'status'
                                );
                              }}
                            />
                          ) : (
                            <BlankTile />
                          );
                        });
                        return { cell };
                      })
                    ];
                    return {
                      key: `${c.instanceId}-${a.key}`,
                      cells,
                      otherData
                    };
                  }),
                  // This is an empty row to provide a bit more spacing when being expanded
                  {
                    key: `${c.instanceId}-spacer`,
                    cells: [
                      { cell: <SpacerTile /> },
                      { cell: <SpacerTile /> },
                      ...sortedTasks.map((x) => {
                        if (x.id.startsWith(ANCHOR_GAP_MOCK_ID_PREFIX)) {
                          return { cell: <AnchorTile info={x} isSpacer /> };
                        }
                        return { cell: <SpacerTile /> };
                      })
                    ],
                    otherData
                  }
                ])
              : [])
          ];
        })
      )
    );
  }, [d, expanded, t, anchorPosition, anchorName, trackingFns, tpl?.data.title]);

  const rows = useApplySearchAndSortRows(allRows, search, sort);

  useErrorLogger(`drilldown report - ${type}: error`, error);

  if (d && d.type !== 'everyone') {
    return null;
  }
  return (
    <>
      <div>
        {loading && <ReportDrilldownLoader />}
        {!loading && rows && !!rows.length && (
          <TableContainer>
            <DataTable2With2StickyColumns
              caption={'Completion for all tasks table'}
              sticky={{
                paddingRight: 1,
                firstColumnWidth: LEFT_COL_WIDTH
              }}
              rows={rows}
            />
          </TableContainer>
        )}
        {!loading && rows && !rows.length && <ReportDrilldownEmptyState />}
        {!loading && !rows && error && <ReportDrilldownError />}
      </div>

      {peopleDialogTask && d && (
        <CardItemDialog
          open={true}
          onClose={() => setPeopleDialogTask(null)}
          headerLeft={
            <Typography variant="h4">{getItemDisplayName(peopleDialogTask)}</Typography>
          }
          dialogTable={
            <TimelineItemExecutionTableLazy
              type="task"
              instanceIds={d.data.containers.map((c) => c.instanceId)}
              tplId={peopleDialogTask.tplId}
              actor={peopleDialogTask.actor}
              itemTplId={peopleDialogTask.id}
              onShowInstanceDetails={(inst) => {
                setDialogInstance({
                  tplId: inst.data.templateId,
                  instanceId: inst.id
                });
              }}
            />
          }
        />
      )}
      {dialogInstance && (
        <ParticipantDialogLazy
          open={true}
          onClose={() => setDialogInstance(null)}
          tplId={dialogInstance.tplId}
          instanceId={dialogInstance.instanceId}
        />
      )}
    </>
  );
};
